/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { css, jsx } from '@compiled/react';

import { SmartCardProvider } from '@atlaskit/link-provider/smart-card-provider';

import { LinkListCompact } from './link-list-compact';
import { LinkListFull } from './link-list-full';
import type { LinkListSize, OnLinkClick } from './types';

interface LinkListProps {
	/** Max items shown after the user clicks "Show more". Defaults to all items when omitted. */
	expandedMaxSources?: number;
	headerVisible?: boolean;
	/** Max items shown in the initial, collapsed state. */
	maxSources?: number;
	onLinkClick?: OnLinkClick;
	/**
	 * Called when the "Show more" expand toggle is clicked to reveal additional
	 * items (i.e. when expanding, not when collapsing).
	 */
	onShowMoreClick?: () => void;
	/** Called when the "View all" link is clicked. Navigation still happens via `viewAllUrl`. */
	onViewAllLinkClick?: () => void;
	/** Open source links in a new tab without changing other callers. */
	openLinksInNewTab?: boolean;
	/**
	 * Whether to show the show more/less expand button when the number of URLs
	 * exceeds `maxSources`. Defaults to `true`. Set to `false` to show all
	 * items up to `maxSources` with no expand/collapse toggle.
	 */
	showExpand?: boolean;
	size?: LinkListSize;
	/**
	 * Identifies the surface/product the link list is rendered in (e.g.
	 * `'luminaSAIN'`). Added to the analytics context and forwarded onto events
	 * fired from within the link list (e.g. the errored event).
	 */
	source?: string;
	/**
	 * Optional titles corresponding 1:1 to `urls` (i.e. `titles[i]` is the title
	 * for `urls[i]`). Callers must keep the arrays index-aligned, using an empty
	 * string when a url has no known title.
	 */
	titles?: string[];
	urls: string[];
	viewAllUrl?: string;
}

const containerRoot = css({
	width: '100%',
	containerType: 'inline-size',
	containerName: 'link-list-knowledge-card-container',
});

const showCompact = css({
	display: 'block',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-container-queries
	'@container link-list-knowledge-card-container (min-width: 481px)': {
		display: 'none',
	},
});

const showFull = css({
	display: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-container-queries
	'@container link-list-knowledge-card-container (min-width: 481px)': {
		display: 'block',
	},
});

export const DEFAULT_MAX_SOURCES = 4;

export const LinkList = ({
	urls,
	titles,
	onLinkClick,
	openLinksInNewTab,
	size,
	maxSources,
	expandedMaxSources,
	showExpand,
	viewAllUrl,
	onViewAllLinkClick,
	onShowMoreClick,
	source,
}: LinkListProps): JSX.Element => {
	return (
		<div css={containerRoot}>
			<SmartCardProvider>
				<div css={showCompact}>
					<LinkListCompact
						urls={urls}
						titles={titles}
						onLinkClick={onLinkClick}
						openLinksInNewTab={openLinksInNewTab}
						size={size}
						maxSources={maxSources}
						expandedMaxSources={expandedMaxSources}
						showExpand={showExpand}
						viewAllUrl={viewAllUrl}
						onViewAllLinkClick={onViewAllLinkClick}
						onShowMoreClick={onShowMoreClick}
						source={source}
					/>
				</div>
				<div css={showFull}>
					<LinkListFull
						urls={urls}
						titles={titles}
						onLinkClick={onLinkClick}
						openLinksInNewTab={openLinksInNewTab}
						maxSources={maxSources}
						expandedMaxSources={expandedMaxSources}
						showExpand={showExpand}
						viewAllUrl={viewAllUrl}
						onViewAllLinkClick={onViewAllLinkClick}
						onShowMoreClick={onShowMoreClick}
						source={source}
					/>
				</div>
			</SmartCardProvider>
		</div>
	);
};
